import { createContext, type ReactNode, useContext, useMemo, useState } from "react";
import { Link, useLocation } from "react-router";
import { isJobActive, useCreatePlanJob, useLastJob, usePlanJob } from "@/api/queries";
import { CheckInModal, checkInPending, planIsStale } from "@/checkin/CheckInModal";
import { Copy, type CopyKey } from "@/copy/copy";
import { ErrorPanel, errorText } from "@/components/ErrorPanel";
import { Spinner } from "@/components/Spinner";
import type { FamilyDocument } from "@/generated/backend/v1/family";
import { type PlanJob, PlanJob_State, StoredPlan } from "@/generated/backend/v1/plan";
import type { User } from "@/generated/backend/v1/user";
import type { Family } from "@/generated/core/v1/family";
import { FinancialPlanData } from "@/generated/core/v1/financial_plan";
import { formatDate, isSet, type PDate, todayLocal } from "@/plan-logic/lib/dates";
import { buildEntityIndex, type EntityIndex, startDate } from "@/plan-logic/plan-view";
import { PageGuideButton, useAutoGuide } from "@/guides/GuideProvider";
import { type GuideKey, isGuideKey } from "@/plan-logic/site/guides";
import { useAppData } from "../shell/AppData";
import { navKeyForPath } from "../shell/nav";
import { isRebuilding, rebuildFailedJob } from "../shell/PlanRebuildStatus";
import { RetiredBanner } from "./RetiredBanner";

/** Everything a page renders from: the stored plan's two runs and the family document. */
export type PlanCtx = {
  stored: StoredPlan;
  abi: FinancialPlanData;
  naive?: FinancialPlanData;
  doc: FamilyDocument;
  family: Family;
  index: EntityIndex;
  user?: User;
  /** The user's "today" (the backend's current date, else the browser's). */
  today: PDate;
  /** The plan's "today" (its start date). */
  planStart: PDate;
  /**
   * The plan is out of date (`planIsStale`: today is a month or more past its start, or a check-in
   * is due): words like "away" and "next month" then count from `planStart`, and say so.
   */
  stale?: boolean;
  /** False on pages rendered without a plan yet (`requirePlan={false}`); the plan is then empty. */
  hasPlan?: boolean;
};

const Ctx = createContext<PlanCtx | null>(null);

const UPDATE_DATA_URL = "/app/update-data";

export function usePlanCtx(): PlanCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("usePlanCtx must be used inside <PlanPage>");
  return ctx;
}

/** A test hook: renders children with a fixed plan context. */
export function PlanCtxProvider({ value, children }: { value: PlanCtx; children: ReactNode }) {
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/**
 * The page's title line (prototype v43/v44): the hero heading with its gold rule, the sub-line held
 * to half the page, and "Page guide" on the right when the page has a guide (v45).
 */
export function PageHead({
  title,
  sub,
  params,
  right,
  guide,
}: {
  title: CopyKey;
  sub?: CopyKey;
  params?: Record<string, string | number>;
  right?: ReactNode;
  guide?: GuideKey;
}) {
  const head = (
    <>
      <Copy k={title} params={params} as="h1" className="pgh" />
      {sub && (
        <Copy
          k={sub}
          as="p"
          className="pgsub"
          style={{ color: "var(--ink2)", marginBottom: right ? 0 : 22 }}
        />
      )}
    </>
  );
  const button = guide && <PageGuideButton page={guide} />;
  if (!right)
    return (
      <>
        {button}
        {head}
      </>
    );
  return (
    <div className="cf-bar" style={{ marginBottom: 18 }}>
      <div>{head}</div>
      {right}
      {button}
    </div>
  );
}

/** "Plan as of 1 October 2026", then whether it's being rebuilt, and the failed-update banner. */
export function PlanStatus({ stored, doc }: { stored: StoredPlan; doc: FamilyDocument }) {
  const asOf = stored.startDate ?? stored.plans?.abiPlan?.meta?.startDate;
  // The same gate as the "updating" dot and the end-of-update toast, so the three agree.
  const failed = rebuildFailedJob(doc, stored);
  return (
    <div className="wp-planstatus" data-testid="plan-status">
      <div className="wp-asofrow">
        {isSet(asOf) && (
          <Copy k="plan.asOf" params={{ date: formatDate(asOf) }} className="wp-asof" />
        )}
        {isRebuilding(doc, stored) && (
          <span className="wp-sync" role="status" data-testid="plan-updating">
            <span className="wp-dot" aria-hidden="true" />
            <Copy k="plan.updating" />
          </span>
        )}
      </div>
      {failed && <UpdateFailedBanner job={failed} asOf={asOf} />}
    </div>
  );
}

/**
 * The household's last change saved, but the plan run after it failed (`StoredPlan.failed_job`):
 * the plan shown is older than their data. Stays until a newer plan succeeds. In the banner family
 * (amber) rather than a toast, because it lasts across visits and pages and needs its two ways out:
 * Update Data, or running the plan again.
 */
function UpdateFailedBanner({ job, asOf }: { job: PlanJob; asOf: PDate | undefined }) {
  const createJob = useCreatePlanJob();
  const onUpdateData = useLocation().pathname.startsWith(UPDATE_DATA_URL);
  return (
    <div className="shortban wp-failban" role="alert" data-testid="plan-update-failed">
      <span className="bi" aria-hidden="true">
        !
      </span>
      <div>
        <Copy k="plan.updateFailedTitle" as="b" />
        <p>
          {isSet(asOf) && <Copy k="plan.updateFailedAsOf" params={{ date: formatDate(asOf) }} />}{" "}
          {job.errorMessage}
        </p>
        {createJob.isError && <p className="wp-form-err">{errorText(createJob.error)}</p>}
      </div>
      <div className="wp-banacts">
        {!onUpdateData && (
          <Link className="go" to={UPDATE_DATA_URL}>
            <Copy k="plan.updateFailedFix" />
          </Link>
        )}
        <button
          type="button"
          className="go"
          onClick={() => createJob.mutate()}
          disabled={createJob.isPending}
          data-testid="plan-retry"
        >
          <Copy k="plan.retry" />
        </button>
      </div>
    </div>
  );
}

/**
 * The plan is out of date (`PlanCtx.stale`): it describes the household from its start, not from
 * today. Offers the check-in when one is due, else a rebuild from today (POST /api/plan-jobs). Not
 * while a newer plan is being built or the last one failed (their own status says it), nor on
 * Update Data, whose check-in gate offers it.
 */
export function StalePlanBanner({ ctx }: { ctx: PlanCtx }) {
  const createJob = useCreatePlanJob();
  const [checking, setChecking] = useState(false);
  const onUpdateData = useLocation().pathname.startsWith(UPDATE_DATA_URL);
  if (!ctx.stale || ctx.hasPlan === false || onUpdateData) return null;
  if (isRebuilding(ctx.doc, ctx.stored) || rebuildFailedJob(ctx.doc, ctx.stored)) return null;
  const due = checkInPending(ctx.family, ctx.abi, ctx.today);
  const date = formatDate(ctx.planStart);
  return (
    <div className="shortban info" role="status" data-testid="plan-stale">
      <span className="bi" aria-hidden="true">
        i
      </span>
      <div>
        <Copy k="plan.staleTitle" as="b" params={{ date }} />
        <Copy k={due ? "plan.staleBody" : "plan.staleBodyRerun"} as="p" params={{ date }} />
        {createJob.isError && <p className="wp-form-err">{errorText(createJob.error)}</p>}
      </div>
      <div className="wp-banacts">
        {due ? (
          <button
            type="button"
            className="go"
            onClick={() => setChecking(true)}
            data-testid="plan-stale-checkin"
          >
            <Copy k="plan.staleCheckIn" />
          </button>
        ) : (
          <button
            type="button"
            className="go"
            onClick={() => createJob.mutate()}
            disabled={createJob.isPending}
            data-testid="plan-stale-rerun"
          >
            <Copy k="plan.staleRerun" />
          </button>
        )}
      </div>
      {checking && (
        <CheckInModal
          doc={ctx.doc}
          abi={ctx.abi}
          today={ctx.today}
          onClose={() => setChecking(false)}
        />
      )}
    </div>
  );
}

/** No plan stored yet: building (with the job's progress), or the job failed. */
export function NoPlan() {
  const lastJob = useLastJob();
  const createJob = useCreatePlanJob();
  // Poll the job that is building the first plan; it refreshes the plan when it finishes.
  usePlanJob(isJobActive(lastJob) ? lastJob?.id : undefined);
  const failed = lastJob?.state === PlanJob_State.FAILED;
  const building = !failed && (isJobActive(lastJob) || !lastJob);
  const onUpdateData = useLocation().pathname.startsWith(UPDATE_DATA_URL);
  return (
    <div className="card pad wp-noplan" data-testid={failed ? "plan-failed" : "no-plan"}>
      {building && <div className="wp-spin" aria-hidden="true" />}
      <Copy k="plan.noPlanTitle" as="h2" />
      <Copy
        k={
          !failed
            ? "plan.noPlanSub"
            : onUpdateData
              ? "plan.noPlanFailedSubHere"
              : "plan.noPlanFailedSub"
        }
        as="p"
        className="mut"
      />
      {failed && lastJob?.errorMessage && <p className="mut">{lastJob.errorMessage}</p>}
      {(failed || !lastJob) && (
        <div className="wp-noplan-acts">
          <button
            type="button"
            className="btn"
            onClick={() => createJob.mutate()}
            disabled={createJob.isPending}
            data-testid="plan-retry"
          >
            <Copy k="plan.retry" />
          </button>
          {failed && !onUpdateData && (
            <Link className="btn g" to={UPDATE_DATA_URL} data-testid="plan-failed-update-data">
              <Copy k="plan.updateFailedFix" />
            </Link>
          )}
        </div>
      )}
      {createJob.isError && <p className="wp-form-err">{errorText(createJob.error)}</p>}
    </div>
  );
}

/**
 * The frame every plan page sits in: loads the family and plan (from the shell), handles loading,
 * error, "no plan yet" and failed states, shows the page head and the plan's "as of" line, then
 * renders the page with a PlanCtx.
 */
export function PlanPage({
  title,
  sub,
  params,
  headRight,
  testId,
  children,
  bare = false,
  requirePlan = true,
  retiredBanner,
  banners,
}: {
  title?: CopyKey;
  sub?: CopyKey;
  params?: Record<string, string | number>;
  headRight?: ReactNode;
  testId: string;
  children: ReactNode | ((ctx: PlanCtx) => ReactNode);
  /** Skip the page head (Home has its own). */
  bare?: boolean;
  /** Render with the family alone when there's no plan yet (Update Data fixes a failed plan). */
  requirePlan?: boolean;
  /**
   * Show the retired banner while a member is retired, with a plan or without one (a retired
   * household's plan usually fails): "fix" with its link to Update Data (Home), "plain" without
   * (Update Data itself).
   */
  retiredBanner?: "fix" | "plain";
  /** The page's own banners (Home's runs-short), placed between the plan's status and the retired banner. */
  banners?: (ctx: PlanCtx) => ReactNode;
}) {
  const { user, family, plan } = useAppData();
  const { pathname } = useLocation();
  const navKey = navKeyForPath(pathname);
  const guide = isGuideKey(navKey) ? navKey : undefined;
  const doc = family.data;
  const stored = plan.data;

  const userToday = user.data?.currentDate;
  const today = useMemo(() => (isSet(userToday) ? userToday : todayLocal()), [userToday]);

  const ctx = useMemo<PlanCtx | null>(() => {
    const abi = stored?.plans?.abiPlan;
    if (!doc?.family) return null;
    if (!stored || !abi) {
      if (requirePlan) return null;
      return {
        hasPlan: false,
        stored: stored ?? StoredPlan.create(),
        abi: FinancialPlanData.create(),
        doc,
        family: doc.family,
        index: buildEntityIndex(doc.family),
        user: user.data,
        today,
        planStart: today,
      };
    }
    return {
      hasPlan: true,
      stored,
      abi,
      naive: stored.plans?.naivePlan,
      doc,
      family: doc.family,
      index: buildEntityIndex(doc.family, abi, stored.plans?.naivePlan),
      user: user.data,
      today,
      planStart: startDate(abi) ?? today,
      stale: planIsStale(doc.family, abi, startDate(abi) ?? today, today),
    };
  }, [doc, stored, user.data, today, requirePlan]);

  // Abi's guide opens the first time the page is shown with its content (a plan, or Update Data's
  // household without one), so its anchored steps have something to point at.
  useAutoGuide(guide, !!ctx && !family.isPending && !plan.isPending);

  // Banners come in one order on every page: the plan's status (as of, a failed update, out of
  // date), the page's own (runs short), then the retired banner.
  const retired = retiredBanner && doc?.family && !family.isPending && (
    <RetiredBanner family={doc.family} today={today} fix={retiredBanner === "fix"} />
  );
  let body: ReactNode;
  if (family.isPending || plan.isPending)
    body = (
      <>
        {retired}
        <Spinner label="Loading your plan" />
      </>
    );
  else if (family.isError)
    body = <ErrorPanel error={family.error} onRetry={() => family.refetch()} />;
  else if (plan.isError)
    body = (
      <>
        {retired}
        <ErrorPanel error={plan.error} onRetry={() => plan.refetch()} />
      </>
    );
  else if (!ctx)
    body = (
      <>
        <NoPlan />
        {retired}
      </>
    );
  else
    body = (
      <Ctx.Provider value={ctx}>
        {ctx.hasPlan === false ? <NoPlan /> : <PlanStatus stored={ctx.stored} doc={ctx.doc} />}
        <StalePlanBanner ctx={ctx} />
        {banners?.(ctx)}
        {retired}
        {typeof children === "function" ? children(ctx) : children}
      </Ctx.Provider>
    );

  return (
    <section className="page on" data-testid={`page-${testId}`}>
      {!bare && title && (
        <PageHead title={title} sub={sub} params={params} right={headRight} guide={guide} />
      )}
      {body}
    </section>
  );
}
